import api from './axios';

// ── Catalog PDF import (ADMIN/CURATOR only) ───────────────────────────────
// Upload a supplier catalog → backend reads each page with Claude → drafts
// land here for human review → reviewer creates the real products.

const BASE = '/admin/catalog-imports';

/** campaignId (optional): Brandstore campaign the reviewed products are added to. */
/** pickPages: render pages and wait for selectPages before any AI reading. */
export const uploadCatalog = (file, { sellerName, sellerContactInformation, spoc, campaignId, pickPages }, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  if (pickPages) formData.append('pickPages', 'true');
  if (campaignId) formData.append('campaignId', campaignId);
  if (sellerName) formData.append('sellerName', sellerName);
  if (sellerContactInformation) formData.append('sellerContactInformation', sellerContactInformation);
  if (spoc) formData.append('spoc', spoc);
  return api.post(BASE, formData, { onUploadProgress });
};

export const listImports = (page = 0, size = 20) => api.get(BASE, { params: { page, size } });

export const getImport = (id) => api.get(`${BASE}/${id}`);

/** Read only these pages; the rest are skipped and never sent to AI. */
export const selectPages = (id, pageNumbers) => api.post(`${BASE}/${id}/pages/selection`, { pageNumbers });

/** { monthSpendUsd, monthlyBudgetUsd (null = no cap) } */
export const getAiUsage = () => api.get(`${BASE}/usage`);

export const deleteImport = (id) => api.delete(`${BASE}/${id}`);

export const retryImport = (id) => api.post(`${BASE}/${id}/retry`);

export const rereadPage = (id, pageNumber) => api.post(`${BASE}/${id}/pages/${pageNumber}/reread`);

export const listItems = (id, status, page = 0, size = 10) =>
  api.get(`${BASE}/${id}/items`, { params: { status: status || undefined, page, size } });

export const createProducts = (id, itemIds, createBrands) =>
  api.post(`${BASE}/${id}/create-products`, { itemIds, createBrands });

export const updateItem = (itemId, body) => api.put(`${BASE}/items/${itemId}`, body);

export const updateItemStatus = (itemId, status) => api.put(`${BASE}/items/${itemId}/status`, { status });

/**
 * Answer to "Similar item detected":
 *   { decision: 'DIFFERENT' }                                  — not the same; create a new product
 *   { decision: 'KEEP_EXISTING', productId }                   — use the existing product as is
 *   { decision: 'UPDATE_EXISTING', productId, fields, imageMode } — overwrite chosen fields from the
 *     catalog; imageMode KEEP | ADD | REPLACE. Both "existing" choices add it to the campaign.
 */
export const decideDuplicate = (itemId, body) => api.put(`${BASE}/items/${itemId}/duplicate-decision`, body);

/** Folds other drafts of the same catalog into itemId as options (variants). */
export const mergeDrafts = (itemId, itemIds) => api.post(`${BASE}/items/${itemId}/merge`, { itemIds });

/** Re-runs the AI similarity check for a draft (after its name/brand/category changed). Slow: seconds. */
export const recheckSimilarity = (itemId) => api.post(`${BASE}/items/${itemId}/similarity-check`);

// Brandstore lookups for choosing a target campaign (readable by curators and admins).
export const listCompanies = () => api.get('/admin/brandstore/tenants');
export const listCampaigns = (companyId) => api.get('/admin/brandstore/campaigns', { params: { tenantId: companyId } });

/**
 * The draft rendered as the shop's product DTO, without saving anything. Pass the review form's
 * current values (fromForm) to preview unsaved edits; omit to preview the saved draft.
 */
export const previewItem = (itemId, unsavedForm) => api.post(`${BASE}/items/${itemId}/preview`, unsavedForm ?? null);

/** box = { pageNumber, x1, y1, x2, y2 } as fractions (0..1) of the page */
export const addItemImage = (itemId, box) => api.post(`${BASE}/items/${itemId}/images`, box);

export const recropItemImage = (itemId, imageId, box) => api.put(`${BASE}/items/${itemId}/images/${imageId}`, box);

export const deleteItemImage = (itemId, imageId) => api.delete(`${BASE}/items/${itemId}/images/${imageId}`);

export const reorderItemImages = (itemId, imageIds) => api.put(`${BASE}/items/${itemId}/images/order`, { imageIds });

/** Backend returns { message } for catalog-import errors. */
export const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;
